import { TextField } from '@mui/material';
import { InputAdornment } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { UserRoles } from '../../../../../enum/UserRoles';
import { Contact } from '../../../../../model/shared/Contact';
import { CustomAutocomplete } from '../../../../../shared/components/Autocomplete';
import { UploadImage } from '../../../../../shared/components/UploadImage';
import { PasswordVerifySteps } from '../../../../../shared/PasswordVerifySteps';
import { IAllStates } from '../../../../../store/rootReducer';
import { multipartFormData } from '../../../../../utils';
import { AGENCY_USER_URL, PROFILE_CREATE_COMPANY_USER_URL } from '../../../../../utils/endpoints';
import { ErrorToast, SuccessToast, WarningToast } from '../../../../../utils/toasters';
import { ICompanyUser } from '../../dto/ICompanyUser';
import { IProfileModal } from '../../dto/IProfileModal';
import useToggle from '../../../../../shared/hooks/useToggle';

const initialContact: Contact = {
    phone: '',
};

const initialCompanyUser: ICompanyUser = {
    contact: initialContact,
    displayName: '',
    email: '',
    firstName: '',
    isGeneralTermsAccepted: false,
    lastName: '',
    password: '',
    position: '',
    repeatPassword: '',
};

function UserAndRolesModal({ show, closeModal, companyUser, updateList, clientId }: IProfileModal) {
    const { t } = useTranslation();
    const newPasswordShow = useToggle();
    const repeatPasswordShow = useToggle();

    const [state, setState] = useState<ICompanyUser>(initialCompanyUser);
    const [files, setFiles] = useState<Blob[] | MediaSource[]>();

    const userType = useSelector((states: IAllStates) => states.auth.user.userType);

    const isAgency = [UserRoles.AGENCY, UserRoles.AGENCY_MASTER].includes(userType as UserRoles);
    const isEditMode = !!companyUser?.id;

    const UserPositionEnum = useSelector((states: IAllStates) =>
        isAgency ? states.enums.AgencyUserPositionEnum : states.enums.BrandUserPositionEnum
    );

    useEffect(() => {
        setState(companyUser ?? initialCompanyUser);
    }, [companyUser]);

    function buildUrl(baseUrl: string): string {
        if (!clientId) return baseUrl;
        return `${baseUrl}?clientId=${clientId}`;
    }

    function handleSuccess(successKey: string) {
        SuccessToast(t(successKey));
        updateList?.(prev => !prev);
        closeModalAction();
        setFiles([]);
    }

    async function submitMultipart(baseUrl: string, applyClientId = false, payload?: ICompanyUser) {
        const isEdit = !!companyUser?.id;
        const url = applyClientId
            ? buildUrl(isEdit ? `${baseUrl}/${companyUser!.id}` : baseUrl)
            : isEdit
            ? `${baseUrl}/${companyUser!.id}`
            : baseUrl;
        const method = isEdit ? undefined : 'post';
        const successKey = isEdit ? 'general.toasts.successUpdated' : 'general.toasts.successCreateCompanyUser';
        try {
            await multipartFormData(files, url, JSON.stringify(payload ?? state), method);
            handleSuccess(successKey);
        } catch (error) {
            ErrorToast(error);
        }
    }

    function handleChange(evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, isContact = false) {
        const { name, value } = evt.target;
        setState(prev => ({
            ...prev,
            ...(isContact ? { contact: { ...prev.contact, [name]: value } } : { [name]: value }),
        }));
    }

    function changePositionHandler(_paramKey: string, paramValue: string | string[] | number) {
        setState(prev => ({ ...prev, position: paramValue as string }));
    }

    function onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (state.password !== state.repeatPassword) {
            return WarningToast(t('general.toasts.warn_passwords_match'));
        }
        const cleanedState = {
            ...state,
            firstName: state.firstName?.trim(),
            lastName: state.lastName?.trim(),
            email: state.email?.trim(),
            contact: {
                ...state.contact,
                phone: state.contact.phone?.trim(),
            },
        };
        setState(cleanedState);
        isAgency
            ? submitMultipart(AGENCY_USER_URL, false, cleanedState)
            : submitMultipart(PROFILE_CREATE_COMPANY_USER_URL, true, cleanedState);
    }

    function closeModalAction() {
        closeModal?.();
        setState(initialCompanyUser);
    }

    function handleCopy() {
        SuccessToast(t('general.button.copied'));
        navigator.clipboard.writeText(`Username: ${state.email}\nPassword: ${state.password}`);
    }

    return (
        <Modal show={show} onHide={closeModalAction} size="lg">
            <form onSubmit={onSubmit}>
                <Modal.Header>
                    <Modal.Title>
                        <h4 className="m-0">
                            {isEditMode
                                ? t('brand.account.users_and_roles_modal.edit_user')
                                : t('brand.account.users_and_roles_modal.create_user')}
                        </h4>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="px-4">
                    <Row>
                        <Col lg="4">
                            <UploadImage
                                saveFile={setFiles}
                                isDeleteHidden={true}
                                isMultiple={false}
                                isPreviewArray={false}
                                classes="px-0"
                                previewImage={companyUser?.profilePhoto?.path ?? '/icon/img-missImg.png'}
                            />
                        </Col>
                        <Col lg="8" className="mt-4 mt-lg-0">
                            <Form.Group className="mb-3">
                                <TextField
                                    onChange={handleChange}
                                    defaultValue={companyUser?.firstName}
                                    fullWidth
                                    required
                                    label={t('general.first_name')}
                                    variant="outlined"
                                    size="small"
                                    name="firstName"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <TextField
                                    onChange={handleChange}
                                    defaultValue={companyUser?.lastName}
                                    fullWidth
                                    required
                                    label={t('general.last_name')}
                                    variant="outlined"
                                    size="small"
                                    name="lastName"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <CustomAutocomplete
                                    dataArray={UserPositionEnum ?? []}
                                    defaultArray={companyUser?.position ?? undefined}
                                    isWithImage={false}
                                    inputLabel="position"
                                    isMultiple={false}
                                    photoLabel=""
                                    isRequired={true}
                                    queryFilterKey=""
                                    inputLabelTranslation={t('brand.account.users_and_roles_modal.position')}
                                    handleChangeParams={changePositionHandler}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mt-0 mt-lg-4">
                        <Col lg={6}>
                            <Form.Group className="mb-3">
                                <TextField
                                    onChange={handleChange}
                                    autoComplete="username"
                                    defaultValue={companyUser?.email}
                                    fullWidth
                                    required
                                    label={t('general.email')}
                                    variant="outlined"
                                    size="small"
                                    name="email"
                                />
                            </Form.Group>
                        </Col>
                        <Col lg={6}>
                            <Form.Group className="mb-3">
                                <TextField
                                    onChange={e => handleChange(e, true)}
                                    defaultValue={companyUser?.contact.phone}
                                    fullWidth
                                    required
                                    label={t('general.phone_number')}
                                    variant="outlined"
                                    size="small"
                                    name="phone"
                                />
                            </Form.Group>
                        </Col>
                        <Col lg={6}>
                            <Form.Group className="mb-3">
                                <TextField
                                    onChange={handleChange}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment
                                                position="start"
                                                className="cursor-pointer"
                                                onClick={newPasswordShow.toggle}
                                            >
                                                {newPasswordShow.value ? (
                                                    <FaEyeSlash color="gray" />
                                                ) : (
                                                    <FaEye color="gray" />
                                                )}
                                            </InputAdornment>
                                        ),
                                    }}
                                    autoComplete="new-password"
                                    defaultValue={companyUser?.password}
                                    fullWidth
                                    required={!isEditMode}
                                    label={t('general.new_password')}
                                    variant="outlined"
                                    type={newPasswordShow.value ? 'text' : 'password'}
                                    size="small"
                                    name="password"
                                />
                            </Form.Group>
                            {!!state.password?.length && (
                                <Col className="mb-4">
                                    <PasswordVerifySteps password={state.password} />
                                </Col>
                            )}
                        </Col>
                        <Col lg={6}>
                            <Form.Group className="mb-3">
                                <TextField
                                    onChange={handleChange}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment
                                                position="start"
                                                className="cursor-pointer"
                                                onClick={repeatPasswordShow.toggle}
                                            >
                                                {repeatPasswordShow.value ? (
                                                    <FaEyeSlash color="gray" />
                                                ) : (
                                                    <FaEye color="gray" />
                                                )}
                                            </InputAdornment>
                                        ),
                                    }}
                                    autoComplete="new-password"
                                    defaultValue={companyUser?.repeatPassword}
                                    fullWidth
                                    required={!isEditMode}
                                    label={t('general.repeat_password')}
                                    variant="outlined"
                                    type={repeatPasswordShow.value ? 'text' : 'password'}
                                    size="small"
                                    name="repeatPassword"
                                />
                            </Form.Group>
                        </Col>
                        {!state.id && show && (
                            <Col xs={12}>
                                <Alert variant="danger">
                                    {t('brand.account.users_and_roles_modal.pass_disclaimer')}
                                </Alert>
                            </Col>
                        )}
                    </Row>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="outline-primary" onClick={closeModal}>
                        {t('general.button.close')}
                    </Button>
                    {!state.id && show && (
                        <Button onClick={handleCopy} variant="primary" className="btn-sm">
                            {t('general.button.copyCredentials')}
                        </Button>
                    )}
                    <Button type="submit" variant="primary" className="btn-sm">
                        {t('general.button.save')}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}

export default UserAndRolesModal;
